import { create } from "zustand";

import type { BBox, LonLat } from "../../api/types";
import { useUi, type ViewKey } from "../../state/ui";
import { boxOf, extensionPreview, preparedFootprint } from "./footprint";

/** Tracés de la zone libre sur la carte (docs/14 §2.7, points 3, 4 et 9), indépendants du camp affiché :
 *  - l'axe, en deux clics (arrière du fuseau, puis extrémité côté ennemi), avec le fuseau prévu en tirets à la
 *    largeur doctrinale et l'empreinte préparée calculée en direct ;
 *  - l'emprise d'une extension, en deux coins opposés.
 *  Un tracé accepté attend sa confirmation dans la vue Manœuvre (« Créer la zone », « Étendre la zone »). L'état vit
 *  le temps de la session (rien n'est enregistré avant la confirmation). */

export interface AxisContext {
  level: string;
  levelKey: string | null;
  /** Mission de l'ordre (libellé de la puce). */
  mission: string | null;
}

/** `error` : dernier clic refusé (clé de `FOOTPRINT_ERRORS`), dit dans le bandeau ; le tracé continue. */
export type ZoneDrawing =
  | ({ mode: "axis"; points: LonLat[]; hover: LonLat | null; error?: string } & AxisContext)
  | { mode: "extend"; zoneId: string; level: string; ring: LonLat[]; points: LonLat[]; hover: LonLat | null; error?: string };

export interface PendingAxis extends AxisContext {
  start: LonLat;
  end: LonLat;
}

interface ZoneDrawState {
  drawing: ZoneDrawing | null;
  /** Axe accepté, en attente de « Préparer le terrain ». */
  axis: PendingAxis | null;
  /** Extension acceptée, en attente de confirmation. */
  extension: { zoneId: string; bbox: BBox } | null;
  /** Nom du dernier lieu choisi par la recherche : préremplit « Nom de la zone ». */
  placeName: string | null;
  /** Demande d'ouvrir le terrain libre dans la vue Manœuvre (« Créer / Enregistrer et placer le terrain » ou « Placer
   *  le terrain » des Données initiales, ou « Terrain libre… » de l'étape Terrain d'un camp). */
  startRequest: number | null;
  /** Parcours guidé après l'enregistrement d'un dossier en « Nouveau terrain » : la recherche de lieu prend le
   *  curseur (au pointeur fin seulement : au doigt, le clavier de l'iPad couvrirait la carte) et « Tracer l'axe » est
   *  désigné comme l'action attendue, jusqu'au début du tracé. */
  guided: boolean;
  /** Dossier dont le choix « Terrain libre » (Données initiales) attend son nouveau terrain : il garde sa carte (un
   *  camp, ou le terrain libre que le nouveau remplacera) jusque-là. Les Données initiales gardent la carte « Terrain
   *  libre » cochée sur « Nouveau terrain » (« Placer le terrain ») et le bloc de création se rouvre avec la vue
   *  Manœuvre ; effacé par « Garder… », par la création du terrain, par un autre choix (« Camp de manœuvre », un autre
   *  terrain) ou au changement de dossier. Le temps de la session. */
  terrainFor: string | null;
  /** « Réessayer » bloqué jusqu'à cet instant après un refus 429 (`Retry-After`), par zone (ou `create`). */
  retryUntil: Record<string, number>;

  startAxis: (context: AxisContext) => void;
  startExtend: (zoneId: string, level: string, ring: LonLat[]) => void;
  /** Point posé (le tracé continue) ; rend les points posés. */
  addPoint: (at: LonLat) => LonLat[];
  hover: (at: LonLat | null) => void;
  /** Clic refusé : le point n'est pas posé, la règle est dite dans le bandeau. */
  refuse: (error: string) => void;
  cancelDrawing: () => void;
  acceptAxis: (axis: PendingAxis) => void;
  acceptExtension: (zoneId: string, bbox: BBox) => void;
  clearPending: () => void;
  setPlaceName: (name: string | null) => void;
  /** `guided` : demande venue de l'enregistrement des Données initiales (parcours guidé). */
  requestStart: (guided?: boolean) => void;
  /** Fin du parcours guidé (tracé commencé, bloc refermé). */
  endGuide: () => void;
  setTerrainFor: (exerciseId: string | null) => void;
  /** Prend la demande d'ouverture (une seule fois) : vrai si elle est récente, et l'efface. */
  consumeStart: (now?: number) => boolean;
  blockRetry: (key: string, until: number | null) => void;
}

/** Une demande « Zone libre… » plus ancienne est ignorée (la vue Manœuvre ne s'est pas ouverte entre-temps). */
export const START_REQUEST_TTL_MS = 10_000;

export const useZoneDraw = create<ZoneDrawState>()((set, get) => ({
  drawing: null,
  axis: null,
  extension: null,
  placeName: null,
  startRequest: null,
  guided: false,
  terrainFor: null,
  retryUntil: {},

  startAxis: (context) =>
    set({ drawing: { mode: "axis", points: [], hover: null, ...context }, axis: null, extension: null, guided: false }),
  startExtend: (zoneId, level, ring) =>
    set({ drawing: { mode: "extend", zoneId, level, ring, points: [], hover: null }, extension: null, axis: null }),
  addPoint: (at) => {
    const drawing = get().drawing;
    if (!drawing) return [];
    const points = [...drawing.points, at];
    set({ drawing: { ...drawing, points, error: undefined } });
    return points;
  },
  hover: (at) => {
    const drawing = get().drawing;
    if (drawing && (drawing.hover?.[0] !== at?.[0] || drawing.hover?.[1] !== at?.[1])) set({ drawing: { ...drawing, hover: at } });
  },
  refuse: (error) => {
    const drawing = get().drawing;
    if (drawing) set({ drawing: { ...drawing, error } });
  },
  cancelDrawing: () => set({ drawing: null }),
  acceptAxis: (axis) => set({ drawing: null, axis }),
  acceptExtension: (zoneId, bbox) => set({ drawing: null, extension: { zoneId, bbox } }),
  clearPending: () => set({ axis: null, extension: null }),
  setPlaceName: (placeName) => set({ placeName }),
  requestStart: (guided = false) => set({ startRequest: Date.now(), guided }),
  endGuide: () => set({ guided: false }),
  setTerrainFor: (terrainFor) => set({ terrainFor }),
  consumeStart: (now = Date.now()) => {
    const at = get().startRequest;
    if (at === null) return false;
    set({ startRequest: null });
    return now - at <= START_REQUEST_TTL_MS;
  },
  blockRetry: (key, until) =>
    set((s) => {
      const next = { ...s.retryUntil };
      if (until === null) delete next[key];
      else next[key] = until;
      return { retryUntil: next };
    }),
}));

/** Le parcours guidé ne survit ni à la sortie de la vue Manœuvre ni à un changement de dossier : sinon un
 *  « Terrain libre… » ouvert plus tard (sur un camp) prendrait le curseur et ferait pulser « Tracer l'axe » hors du
 *  parcours. */
export function guideEnds(
  prev: { view: ViewKey; exerciseId: string | null },
  next: { view: ViewKey; exerciseId: string | null },
): boolean {
  return (next.view !== prev.view && next.view !== "manoeuvre") || next.exerciseId !== prev.exerciseId;
}

useUi.subscribe((next, prev) => {
  const draw = useZoneDraw.getState();
  if (draw.guided && guideEnds(prev, next)) draw.endGuide();
  // Choix « Terrain libre » en attente : propre à son dossier.
  if (draw.terrainFor !== null && next.exerciseId !== draw.terrainFor) draw.setTerrainFor(null);
});

/** Clic sur la carte pendant un tracé de zone : premier point posé ; second point contrôlé par la même règle que le
 *  serveur (`preparedFootprint`, `extensionPreview`) — accepté (le tracé attend sa confirmation dans la vue
 *  Manœuvre) ou refusé (la règle est dite dans le bandeau, le tracé continue). Rend ce qui s'est passé, ou null hors
 *  tracé. */
export function clickZoneDraw(at: LonLat): "placed" | "accepted" | "refused" | null {
  const state = useZoneDraw.getState();
  const drawing = state.drawing;
  if (!drawing) return null;
  if (drawing.points.length === 0) {
    state.addPoint(at);
    return "placed";
  }
  const first = drawing.points[0]!;
  if (drawing.mode === "axis") {
    const result = preparedFootprint(first, at, drawing.level, drawing.levelKey);
    if (!result.ok) {
      state.refuse(result.error);
      return "refused";
    }
    state.acceptAxis({ start: first, end: at, level: drawing.level, levelKey: drawing.levelKey, mission: drawing.mission });
    return "accepted";
  }
  const bbox = boxOf(first, at);
  const preview = extensionPreview(drawing.ring, bbox, drawing.level);
  if (!preview.ok) {
    state.refuse(preview.error ?? "invalid");
    return "refused";
  }
  state.acceptExtension(drawing.zoneId, bbox);
  return "accepted";
}
